import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getIssueHistoryEventParts } from './zoneIssueHistoryUtils';
import ZoneIssueHistoryTriggerIcon from './ZoneIssueHistoryTriggerIcon';

const TOOLTIP_GAP = 10;
const VIEWPORT_PAD = 8;

const ZoneIssueHistoryIcon = ({ events = [], t }) => {
  const anchorRef = useRef(null);
  const tooltipRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({
    top: 0,
    left: 0,
    placement: 'right',
    arrowTop: '50%',
  });

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const tooltip = tooltipRef.current;
    if (!anchor || !tooltip) {
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    const tooltipWidth = tooltipRect.width || 220;
    const tooltipHeight = tooltipRect.height || 120;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let placement = 'right';
    let left = rect.right + TOOLTIP_GAP;
    let top = rect.top + rect.height / 2 - tooltipHeight / 2;

    const fitsRight = left + tooltipWidth <= vw - VIEWPORT_PAD;
    const fitsLeft = rect.left - TOOLTIP_GAP - tooltipWidth >= VIEWPORT_PAD;

    if (!fitsRight && fitsLeft) {
      placement = 'left';
      left = rect.left - tooltipWidth - TOOLTIP_GAP;
    } else if (!fitsRight && !fitsLeft) {
      placement = 'bottom';
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
      top = rect.bottom + TOOLTIP_GAP;
    }

    if (placement === 'bottom') {
      left = Math.min(
        Math.max(VIEWPORT_PAD, left),
        vw - tooltipWidth - VIEWPORT_PAD
      );
      if (top + tooltipHeight > vh - VIEWPORT_PAD) {
        placement = 'top';
        top = rect.top - tooltipHeight - TOOLTIP_GAP;
      }
    } else {
      top = Math.min(
        Math.max(VIEWPORT_PAD, top),
        vh - tooltipHeight - VIEWPORT_PAD
      );
    }

    const anchorCenterY = rect.top + rect.height / 2;
    const arrowTop = Math.min(
      Math.max(16, anchorCenterY - top),
      Math.max(16, tooltipHeight - 16)
    );

    setCoords({
      top,
      left,
      placement,
      arrowTop: placement === 'bottom' || placement === 'top' ? '50%' : `${arrowTop}px`,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      return undefined;
    }

    updatePosition();
    const frameId = window.requestAnimationFrame(updatePosition);

    return () => window.cancelAnimationFrame(frameId);
  }, [open, events, updatePosition]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleReposition = () => updatePosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, updatePosition]);

  const show = () => setOpen(true);
  const hide = () => setOpen(false);

  if (!events?.length) {
    return null;
  }

  const tooltipContent = (
    <div
      ref={tooltipRef}
      className={`zone-create-edit__issue-history-tooltip zone-create-edit__issue-history-tooltip--portaled zone-create-edit__issue-history-tooltip--${coords.placement}`}
      role="tooltip"
      style={{
        top: coords.top,
        left: coords.left,
        '--issue-history-arrow-top': coords.arrowTop,
      }}
    >
      <span className="zone-create-edit__issue-history-tooltip-title">
        {t('History', { defaultValue: 'History' })}
      </span>
      <ul className="zone-create-edit__issue-history-list">
        {events.map((event, index) => {
          const { type, typeLabel, qty, when } = getIssueHistoryEventParts(
            event,
            t
          );

          return (
            <li
              key={`${event?.type ?? 'event'}-${event?.datetime ?? index}`}
              className={`zone-create-edit__issue-history-item zone-create-edit__issue-history-item--${type}`}
            >
              <span className="zone-create-edit__issue-history-badge">
                <span className="zone-create-edit__issue-history-badge-label">
                  {typeLabel}
                </span>
                <span className="zone-create-edit__issue-history-badge-qty">
                  {qty}
                </span>
              </span>
              {when ? (
                <span className="zone-create-edit__issue-history-when">
                  {when}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <>
      <span
        className="zone-create-edit__issue-history"
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        <button
          ref={anchorRef}
          type="button"
          className="zone-create-edit__issue-history-trigger"
          aria-label={t('Issue history', { defaultValue: 'Issue history' })}
        >
          <ZoneIssueHistoryTriggerIcon />
          {events.length > 1 ? (
            <span className="zone-create-edit__issue-history-count" aria-hidden>
              {events.length}
            </span>
          ) : null}
        </button>
      </span>
      {open ? createPortal(tooltipContent, document.body) : null}
    </>
  );
};

export default ZoneIssueHistoryIcon;
